import { flash, Modal, SubmitButton } from "@xola/ui-kit";
import { AxiosError, AxiosRequestConfig } from "axios";
import { FormField, InputField, requiredString } from "common";
import React from "react";
import { Except } from "type-fest";
import { User } from "types";
import { object } from "yup";
import { api } from "../../../api/api";
import { useUser } from "../../../hooks/useAccount";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";

const validationSchema = object().shape({
    password: requiredString(),
});

export interface ValidatePasswordModalProps {
    isOpen?: boolean;
    onSuccess: () => void;
    onClose: () => void;
}

interface FormValues {
    password: string;
}

const initialValues: FormValues = { password: "" };

export const ValidatePasswordModal = ({ isOpen = false, onSuccess, onClose }: ValidatePasswordModalProps) => {
    return (
        <Modal isOpen={isOpen} size="medium" onClose={onClose}>
            <ModalContent onClose={onClose} onSuccess={onSuccess} />
        </Modal>
    );
};

const ModalContent = ({ onSuccess, onClose }: Except<ValidatePasswordModalProps, "isOpen">) => {
    const user = useUser();
    const config: AxiosRequestConfig = {
        ignoreUnauthorizedErrorInterceptor: true,
        suppressErrorToast: [401],
    };

    const handleSubmit = async (values: FormValues) => {
        if (user?.username && values?.password) {
            config.auth = { username: user?.username, password: values?.password };
        }

        try {
            const { data: userData } = await api.get<User>("/users/me", config);

            if (userData) {
                onSuccess();
            }
        } catch (error: any) {
            if ((error as AxiosError).response?.status === 401) {
                flash.show({ text: "Authentication error. Please specify correct password", color: "danger" });
            }
        }
    };

    return (
        <XolaFormik
            enableReinitialize
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, isSubmitting }) => (
                <>
                    <Modal.Header>Confirm Password</Modal.Header>

                    <Modal.Body>
                        <FormField name="password" label="Password">
                            <InputField type="password" />
                        </FormField>
                    </Modal.Body>

                    <Modal.Footer className="space-x-4">
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        <SubmitButton type="submit" color="primary" isLoading={isSubmitting} onClick={handleSubmit}>
                            Confirm
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};
